// Single source of truth for the lander's NSFW / SFW variation.
//
// Mirrors Match-LP: `?variation=sfw` opts a visit into the SFW funnel; anything
// else (including an absent param) is the default NSFW funnel. The SFW arm can
// additionally be forced from GrowthBook via `createsexfriend-content`'s
// `isSFWOverride` (see below). The resolved value drives three things:
//   1. which branch of the `createsexfriend-content` flag is used (constants +
//      GrowthBookContext),
//   2. the GB `variation` attribute, so every other flag can be targeted
//      per-variation (lib/growthbookClient),
//   3. the `protection=sfw` marker on every platform handoff (utils/api,
//      utils/paywall, the auth screens' OAuth `path`, the Upgate return URL).
//
// Kept dependency-free: utils/paywall imports utils/everflow, so any import
// added here risks a cycle — same rule as utils/authType.

export const VARIATION_NSFW = 'nsfw';
export const VARIATION_SFW = 'sfw';
export const DEFAULT_VARIATION = VARIATION_NSFW;

const VARIATION_PARAM = 'variation';

// ---- GrowthBook SFW override (createsexfriend-content.isSFWOverride) ----
//
// The content flag carries a top-level `isSFWOverride` boolean:
//   true  → the visit runs the SFW funnel regardless of `?variation=`;
//   false → SFW only on `?variation=sfw` (the default behaviour).
//
// The flag lands async, so GrowthBookContext pushes the resolved value in here
// and every consumer — including the non-React handoff helpers below — reads it
// through getVariation().
const SFW_OVERRIDE_FIELD = 'isSFWOverride';

let sfwOverride = false;

// Reads `isSFWOverride` off a raw createsexfriend-content value and applies it.
// Returns the resolved override so callers can use it in the same render.
// GrowthBook may hand booleans back as strings, so both shapes are accepted.
//
// One-way (nsfw -> sfw only): syncing the `variation` attribute re-evaluates the
// content flag, and the payload that comes back for the sfw arm need not repeat
// the switch — clearing the latch there would flip the arm back and forth.
export const applyContentSfwOverride = (rawContent) => {
  const value =
    rawContent && typeof rawContent === 'object'
      ? rawContent[SFW_OVERRIDE_FIELD]
      : false;
  if (value === true || value === 'true') sfwOverride = true;
  return sfwOverride;
};

// The active arm: SFW when GrowthBook forces it, otherwise only on
// `?variation=sfw`. The override never pushes a visit back to NSFW, so the URL
// param keeps working on its own while the flag is off.
export const getVariation = () => {
  if (typeof window === 'undefined') return DEFAULT_VARIATION;
  if (sfwOverride) return VARIATION_SFW;
  return new URLSearchParams(window.location.search).get(VARIATION_PARAM) ===
    VARIATION_SFW
    ? VARIATION_SFW
    : DEFAULT_VARIATION;
};

export const isSfwVariation = () => getVariation() === VARIATION_SFW;

// Stamps the SFW marker onto a platform handoff URL. A no-op on the NSFW arm, so
// callers apply it unconditionally at every handoff instead of branching.
export const applyProtection = (url) => {
  if (isSfwVariation()) url.searchParams.set('protection', 'sfw');
  return url;
};

// Same, for the URLSearchParams the Google OAuth `path` is built from (the
// backend re-parses `path` as a query string, so the marker has to be encoded
// into it rather than appended to the outer URL).
export const applyProtectionParams = (params) => {
  if (isSfwVariation()) params.set('protection', 'sfw');
  return params;
};
